import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import { BackLink } from '../components/ui/BackLink';
import LedgerList from '../components/wallet/LedgerList';
import { getWalletLedger, type LedgerEntry } from '../services/walletService';
import { useAuth } from '../contexts/AuthContext';

const SUPPORT_EMAIL = 'support@wisetree.ai';
const MAX_ROWS = 1000;      // backend hard cap — never request more
const PAGE_SIZE = 25;       // rows revealed per "Show more"

/** YYYY-MM-DD for a date `days` ago / today, for the native date inputs (browser-local). */
const isoDay = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

/**
 * Full transaction history — its OWN page so the wallet page stays focused. Defaults to the last
 * 7 days, filterable by date range, paginated client-side, and capped at 1000 rows from the server.
 * Timestamps render in the user's timezone (default UTC).
 */
const WalletHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const [from, setFrom] = useState<string>(isoDay(7));
  const [to, setTo] = useState<string>(isoDay(0));
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);

  // Date inputs are wall-clock days; convert to absolute instants. `to` is inclusive → +1 day.
  const { sinceMs, untilMs } = useMemo(() => {
    const since = from ? new Date(`${from}T00:00:00`).getTime() : undefined;
    const untilBase = to ? new Date(`${to}T00:00:00`).getTime() : undefined;
    const until = typeof untilBase === 'number' ? untilBase + 24 * 60 * 60 * 1000 : undefined;
    return { sinceMs: Number.isNaN(since as number) ? undefined : since, untilMs: Number.isNaN(until as number) ? undefined : until };
  }, [from, to]);

  const load = useCallback(() => {
    setLoading(true);
    setVisible(PAGE_SIZE);
    getWalletLedger({ limit: MAX_ROWS, sinceMs, untilMs })
      .then(setLedger)
      .catch(() => setLedger([]))
      .finally(() => setLoading(false));
  }, [sinceMs, untilMs]);

  useEffect(() => { load(); }, [load]);

  const shown = ledger.slice(0, visible);
  const capped = ledger.length >= MAX_ROWS;

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <BackLink to="/wallet" label="Back to wallet" />
        <h1 className="mt-6 text-2xl font-bold">Transaction history</h1>
        <p className="mt-1 text-sm text-secondary-500">Your grants, spends, and top-ups — who actioned them, and when in your timezone.</p>

        {/* Date-range filter — defaults to the last 7 days. */}
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-secondary-500">
            From
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
              className="rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2.5 py-1.5 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-secondary-500">
            To
            <input type="date" value={to} min={from} max={isoDay(0)} onChange={(e) => setTo(e.target.value)}
              className="rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2.5 py-1.5 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
          </label>
          <div className="flex gap-1.5 text-xs">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => { setFrom(isoDay(d)); setTo(isoDay(0)); }}
                className="rounded-lg border border-secondary-200 dark:border-secondary-700 px-2.5 py-1.5 text-secondary-600 dark:text-secondary-300 hover:border-secondary-400 dark:hover:border-secondary-500">
                Last {d}d
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-secondary-400">Loading…</p>
        ) : (
          <>
            <p className="mt-5 text-xs text-secondary-400">
              {ledger.length === 0 ? 'No activity in this range.' : `Showing ${shown.length} of ${ledger.length}${capped ? '+' : ''} transactions`}
              {capped && ' (capped at 1000 — narrow the date range to see older activity)'}
            </p>
            <LedgerList entries={shown} timezone={user?.timezone} />
            {visible < ledger.length && (
              <button onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="mt-4 w-full rounded-xl border border-secondary-200 dark:border-secondary-700 py-2.5 text-sm font-medium text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-900 transition-colors">
                Show more
              </button>
            )}
          </>
        )}

        {/* Support footer — any further help. */}
        <div className="mt-10 border-t border-secondary-100 dark:border-secondary-800 pt-5 text-sm text-secondary-500">
          <span className="inline-flex items-center gap-1.5">
            <Mail className="w-4 h-4" />
            Need help with a transaction? Contact{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-secondary-800 dark:text-secondary-100 hover:underline">{SUPPORT_EMAIL}</a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default WalletHistoryPage;
